import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Container,
  Typography,
  makeStyles
} from '@material-ui/core';

import GAException from 'src/components/GAException';

const useStyles = makeStyles((theme) => ({
  root: {
    alignItems: 'center',
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: '60vh',
    padding: theme.spacing(3),
    textAlign: 'center'
  }
}));

/**
 * UI por defecto cuando un error de render tumba el arbol.
 * Va separada del boundary porque los hooks no se pueden usar en clases.
 */
function DefaultFallback({ onRetry }) {
  const classes = useStyles();

  return (
    <Container maxWidth="sm">
      <Box className={classes.root}>
        <Typography
          variant="h3"
          color="textPrimary"
          gutterBottom
        >
          Ocurrio un error inesperado
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
        >
          No pudimos mostrar esta pantalla. Volve a intentarlo o regresa al inicio.
        </Typography>
        <Box
          mt={4}
          display="flex"
          justifyContent="center"
        >
          <Box mr={1}>
            <Button
              color="primary"
              variant="contained"
              onClick={onRetry}
            >
              Reintentar
            </Button>
          </Box>
          <Button
            color="primary"
            variant="outlined"
            onClick={() => window.location.assign('/app')}
          >
            Ir al inicio
          </Button>
        </Box>
      </Box>
    </Container>
  );
}

DefaultFallback.propTypes = {
  onRetry: PropTypes.func.isRequired
};

/**
 * Captura errores de render de sus hijos y evita que React desmonte el arbol
 * completo (pantalla en blanco). Debe ser clase: no existe equivalente en hooks
 * para componentDidCatch / getDerivedStateFromError.
 *
 * @param {*} props.resetKey - al cambiar, limpia el estado de error. Pasar el
 *                             pathname para que el boundary no quede pegado al navegar.
 * @param {node} props.fallback - UI alternativa opcional.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    try {
      GAException(error);
    } catch (e) {
      // analytics no debe romper el boundary
    }
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info && info.componentStack);
  }

  componentDidUpdate(prevProps) {
    const { resetKey } = this.props;
    const { hasError } = this.state;

    if (hasError && prevProps.resetKey !== resetKey) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ hasError: false });
    }
  }

  handleRetry() {
    this.setState({ hasError: false });
  }

  render() {
    const { children, fallback } = this.props;
    const { hasError } = this.state;

    if (hasError) {
      return fallback || <DefaultFallback onRetry={this.handleRetry} />;
    }

    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node,
  fallback: PropTypes.node,
  // eslint-disable-next-line react/forbid-prop-types
  resetKey: PropTypes.any
};

ErrorBoundary.defaultProps = {
  children: null,
  fallback: null,
  resetKey: null
};

export default ErrorBoundary;
